import React, { useEffect, useMemo, useState } from "react";
import { LoadingDiv, SweetAlertShow } from "../master/Master";
import Header from "../master/SideBar";
import Nav from "../master/NavBar";
import * as XLSX from "xlsx";
import Master from "./Master";
import { busStopImport } from "../../api/Transport";

export default function BusStopImport() {
  const [loading, setLoading] = useState(false);

  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState("");

  const [excelHeaders, setExcelHeaders] = useState([]);
  const [excelSample, setExcelSample] = useState({});
  const [excelData, setExcelData] = useState([]);
  const [importStatus, setImportStatus] = useState({});
  const [progress, setProgress] = useState(0);
  const [isImporting, setIsImporting] = useState(false);
  const [editableData, setEditableData] = useState([]);
  const [mappingRows, setMappingRows] = useState([
    { field: "bus_number", excel_column: "" },
  ]);

  useEffect(() => {
    const parsed = JSON.parse(localStorage.getItem("sessions") ?? "[]");
    setSessions(parsed);
    const active = parsed?.find((s) => Number(s.status) === 1)?.id;
    if (active) setSessionId(active);
  }, []);

  const busStopFields = [
    { key: "bus_number", value: "Bus Number" },
    { key: "driver_name", value: "Driver" },
    { key: "driver_number", value: "Driver Mobile No" },
    { key: "stop_name", value: "Bus Stop" },
    { key: "stop_fee", value: "Amount" },
    { key: "status", value: "Status" },
  ];

  const numericFields = ["stop_fee"];

  const fieldAliases = {
    bus_number: ["bus number", "busnumber", "bus no", "bus"],
    driver_name: ["driver", "driver name", "drivers name"],
    driver_number: [
      "driver mobile no",
      "driver mobile",
      "driver number",
      "mobile no",
      "mobile",
      "contact",
    ],
    stop_name: ["bus stope", "bus stop", "stop name", "stop", "route stop"],
    stop_fee: ["amount", "stop fee", "fee", "stop amount"],
    status: ["status"],
  };

  const normalize = (str) =>
    String(str || "")
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "");

  const downloadBlankTemplate = () => {
    const headers = [
      "BUS NUMBER *",
      "DRIVER",
      "DRIVER MOBILE NO",
      "BUS STOP *",
      "AMOUNT *",
      "STATUS",
    ];

    const worksheet = XLSX.utils.aoa_to_sheet([headers]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Template");
    XLSX.writeFile(workbook, "bus_stop_import_template.xlsx");
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validTypes = [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel",
    ];

    if (!validTypes.includes(file.type)) {
      SweetAlertShow("error", "Invalid File", "Please upload Excel file");
      return;
    }

    const reader = new FileReader();

    reader.onload = (evt) => {
      const data = new Uint8Array(evt.target.result);
      const workbook = XLSX.read(data, { type: "array" });
      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];

      const jsonData = XLSX.utils.sheet_to_json(sheet, {
        header: 1,
        raw: false,
      });

      if (!jsonData.length) return;

      const headers = (jsonData[0] || [])
        .map((h) => String(h || "").trim())
        .filter(Boolean);

      const rows = jsonData
        .slice(1)
        .filter((row) =>
          row.some(
            (cell) =>
              cell !== undefined && cell !== null && String(cell).trim() !== "",
          ),
        );

      const secondRow = rows[0] || [];

      setExcelHeaders(headers);
      setExcelData(rows);
      setImportStatus({});
      setProgress(0);

      const sampleObj = {};
      headers.forEach((h, i) => {
        sampleObj[h] = secondRow[i] || "";
      });
      setExcelSample(sampleObj);

      const autoMap = busStopFields
        .map((f) => {
          const possibleNames = [f.value, ...(fieldAliases[f.key] || [])];
          const match = headers.find((h) => {
            const hNorm = normalize(h);
            return possibleNames.some((name) => hNorm === normalize(name));
          });
          if (!match) return null;
          return { field: f.key, excel_column: match };
        })
        .filter(Boolean);

      setMappingRows(
        autoMap.length ? autoMap : [{ field: "", excel_column: "" }],
      );
    };

    reader.readAsArrayBuffer(file);
  };

  const handleAddRow = () => {
    setMappingRows([...mappingRows, { field: "", excel_column: "" }]);
  };

  const handleRemoveRow = (index) => {
    setMappingRows(mappingRows.filter((_, i) => i !== index));
  };

  const handleChange = (index, key, value) => {
    setMappingRows((prev) => {
      let updated = prev.map((row, i) =>
        i === index ? { ...row, [key]: value } : row,
      );

      if (key === "excel_column") {
        updated = updated.map((row, i) =>
          i !== index && row.excel_column === value
            ? { ...row, excel_column: "" }
            : row,
        );
      }
      if (key === "field") {
        updated = updated.map((row, i) =>
          i !== index && row.field === value ? { ...row, field: "" } : row,
        );
      }
      return [...updated];
    });
  };

  const usedFields = mappingRows.map((r) => r.field);

  const getMappedData = () => {
    if (!excelData.length) return [];

    return excelData.map((row) => {
      const obj = {};

      mappingRows.forEach((map) => {
        if (!map.field || !map.excel_column) return;
        const colIndex = excelHeaders.findIndex((h) => h === map.excel_column);
        let value = colIndex !== -1 ? (row[colIndex] ?? "") : "";

        if (numericFields.includes(map.field)) {
          value =
            value === "" ? "" : Number(String(value).replace(/,/g, "")) || 0;
        }

        const skipUppercaseFields = ["driver_number", ...numericFields];
        if (
          typeof value === "string" &&
          !skipUppercaseFields.includes(map.field)
        ) {
          value = value.trim().toUpperCase();
        }

        obj[map.field] = value;
      });

      return obj;
    });
  };

  const mappedData = useMemo(
    () => getMappedData(),
    [mappingRows, excelData, excelHeaders],
  );

  useEffect(() => {
    setEditableData(mappedData || []);
  }, [mappedData]);

  const handleCellChange = (rowIndex, field, value) => {
    const updated = [...editableData];
    updated[rowIndex][field] = value;
    setEditableData(updated);
  };

  const activeMappings = useMemo(
    () => mappingRows.filter((m) => m.field && m.excel_column),
    [mappingRows],
  );

  const validateRow = (row) => {
    const errors = [];

    if (!row.bus_number) errors.push("Bus number required");
    if (!row.stop_name) errors.push("Bus stop required");
    if (
      row.stop_fee === "" ||
      row.stop_fee === undefined ||
      isNaN(Number(row.stop_fee))
    )
      errors.push("Amount required");
    if (row.driver_number && !/^[0-9]{10}$/.test(String(row.driver_number)))
      errors.push("Invalid mobile");

    return errors;
  };

  const validationErrors = useMemo(
    () => editableData.map((row) => validateRow(row)),
    [editableData],
  );

  const runImport = async (rowsToImport) => {
    let statusObj = { ...importStatus };
    setLoading(true);
    const chunkSize = 40;

    for (let i = 0; i < rowsToImport.length; i += chunkSize) {
      const chunk = rowsToImport.slice(i, i + chunkSize);
      await Promise.allSettled(
        chunk.map(async (row) => {
          try {
            const formData = new FormData();
            formData.append("session_id", sessionId);
            formData.append("bus_number", row.data.bus_number ?? "");
            formData.append("driver_name", row.data.driver_name ?? "");
            formData.append("driver_number", row.data.driver_number ?? "");
            formData.append("stop_name", row.data.stop_name ?? "");
            formData.append("stop_fee", row.data.stop_fee ?? "");

            const res = await busStopImport(formData);
            statusObj[row.index] = {
              status: res?.data?.status ? "success" : "error",
              message: Array.isArray(res?.data?.message)
                ? res.data.message.join(", ")
                : res?.data?.message || "Failed",
            };
          } catch (err) {
            const msg =
              err?.response?.data?.message || err?.message || "Import Failed";
            statusObj[row.index] = {
              status: "error",
              message: Array.isArray(msg) ? msg.join(", ") : msg,
            };
          }
        }),
      );

      setImportStatus({ ...statusObj });
      const processed = Object.keys(statusObj).length;
      setProgress(Math.round((processed / rowsToImport.length) * 100));
      await new Promise((resolve) => setTimeout(resolve, 1000));
    }

    setLoading(false);
    return statusObj;
  };

  const handleStartImport = async () => {
    if (isImporting) return;

    if (!sessionId) {
      return SweetAlertShow(
        "error",
        "Missing Session",
        "Please select a session first",
      );
    }

    const requiredFields = ["bus_number", "stop_name", "stop_fee"];
    const missingFields = requiredFields.filter(
      (rf) => !mappingRows.some((m) => m.field === rf),
    );
    if (missingFields.length) {
      return SweetAlertShow(
        "error",
        "Missing Mapping",
        missingFields.join(", "),
      );
    }

    const rows = editableData
      .map((d, i) => ({ index: i, data: d, errors: validationErrors[i] }))
      .filter(
        (row) =>
          row.errors.length === 0 &&
          importStatus[row.index]?.status !== "success",
      );

    if (!rows.length) {
      return SweetAlertShow(
        "error",
        "No Valid Rows",
        "Please fix validation errors",
      );
    }

    setProgress(0);
    setIsImporting(true);

    const finalStatus = await runImport(rows);

    const successCount = Object.values(finalStatus || {}).filter(
      (s) => s.status === "success",
    ).length;
    const failedCount = Object.values(finalStatus || {}).filter(
      (s) => s.status === "error",
    ).length;

    SweetAlertShow(
      "success",
      "Import Completed",
      `Success: ${successCount}, Failed: ${failedCount}`,
    );
    setIsImporting(false);
  };

  const handleRetryFailed = async () => {
    if (!sessionId) {
      return SweetAlertShow(
        "error",
        "Missing Session",
        "Please select a session first",
      );
    }

    const failedRows = editableData
      .map((d, i) => ({ index: i, data: d, errors: validationErrors[i] }))
      .filter(
        (row) =>
          row.errors.length === 0 &&
          importStatus[row.index]?.status === "error",
      );

    if (!failedRows.length) {
      return SweetAlertShow("info", "Info", "No failed rows");
    }

    setIsImporting(true);
    await runImport(failedRows);
    setIsImporting(false);
  };

  const handleRetrySingle = async (index) => {
    if (!sessionId) {
      return SweetAlertShow(
        "error",
        "Missing Session",
        "Please select a session first",
      );
    }
    if (validationErrors[index]?.length) {
      return SweetAlertShow(
        "error",
        "Validation Error",
        validationErrors[index].join(", "),
      );
    }
    setIsImporting(true);
    await runImport([{ index, data: editableData[index] }]);
    setIsImporting(false);
  };

  const downloadFailedRows = () => {
    const failedRows = editableData
      .map((row, index) => ({
        ...row,
        errors: validationErrors[index].join(", "),
        import_error: importStatus[index]?.message || "",
      }))
      .filter(
        (_, index) =>
          validationErrors[index]?.length ||
          importStatus[index]?.status === "error",
      );

    if (!failedRows.length) {
      return SweetAlertShow("info", "Info", "No failed rows");
    }

    const worksheet = XLSX.utils.json_to_sheet(failedRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Failed Rows");
    XLSX.writeFile(workbook, "failed_bus_stop_import_rows.xlsx");
  };

  const successCount = Object.values(importStatus).filter(
    (s) => s.status === "success",
  ).length;
  const failedCount = Object.values(importStatus).filter(
    (s) => s.status === "error",
  ).length;
  const validationCount = validationErrors.filter((e) => e.length).length;

  return (
    <>
      {loading && <LoadingDiv />}

      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header />
          <div className="layout-page">
            <Nav />
            <div className="content-wrapper">
              <div className="container-fluid mt-2">
                <div className="row">
                  <Master />
                  <div className="col-12">
                    <div className="card">
                      <div className="card-body">
                        <h5>Bus Stop Import</h5>
                        <div className="row g-4 align-items-center">
                          <div className="col-sm-4">
                            <div className="form-floating form-floating-outline">
                              <select
                                className="form-select"
                                value={sessionId}
                                onChange={(e) => setSessionId(e.target.value)}
                              >
                                <option value="" disabled>
                                  Select Session
                                </option>
                                {sessions.map((item) => (
                                  <option key={item.id} value={item.id}>
                                    {item.session}:{" "}
                                    {Number(item.status) === 1
                                      ? "Active"
                                      : "Inactive"}
                                  </option>
                                ))}
                              </select>
                              <label>Session</label>
                            </div>
                          </div>

                          <div className="col-sm-4">
                            <button
                              className="btn btn-info"
                              onClick={downloadBlankTemplate}
                            >
                              Download Template
                            </button>
                          </div>

                          <div className="col-sm-4">
                            <div className="form-floating form-floating-outline">
                              <input
                                type="file"
                                className="form-control"
                                onChange={handleFileUpload}
                              />
                              <label className="ms-5">Upload Excel</label>
                            </div>
                          </div>
                        </div>

                        <div className="row g-4 mt-2">
                          <div className="col-12">
                            <div className="d-flex justify-content-end mb-2">
                              <button
                                className="btn btn-outline-primary"
                                onClick={handleAddRow}
                                disabled={!excelHeaders.length}
                              >
                                Add Field
                              </button>
                            </div>

                            <div className="table-responsive">
                              <table className="table table-bordered">
                                <thead>
                                  <tr>
                                    <th>Field</th>
                                    <th>Excel Column</th>
                                    <th>Preview</th>
                                    <th>Action</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {mappingRows?.map((row, index) => (
                                    <tr key={index}>
                                      <td className="p-1">
                                        <select
                                          className="form-select p-1"
                                          value={row.field}
                                          onChange={(e) =>
                                            handleChange(
                                              index,
                                              "field",
                                              e.target.value,
                                            )
                                          }
                                        >
                                          <option value="">Select Field</option>
                                          {busStopFields?.map((f, i) => (
                                            <option
                                              key={i}
                                              value={f.key}
                                              disabled={
                                                usedFields.includes(f.key) &&
                                                row.field !== f.key
                                              }
                                            >
                                              {f.value}
                                            </option>
                                          ))}
                                        </select>
                                      </td>
                                      <td className="p-1">
                                        <select
                                          className="form-select p-1"
                                          value={row.excel_column}
                                          onChange={(e) =>
                                            handleChange(
                                              index,
                                              "excel_column",
                                              e.target.value,
                                            )
                                          }
                                        >
                                          <option value="">
                                            Select Column
                                          </option>
                                          {excelHeaders.map((col, i) => (
                                            <option key={i} value={col}>
                                              {col}
                                            </option>
                                          ))}
                                        </select>
                                      </td>
                                      <td className="p-1">
                                        <input
                                          type="text"
                                          className="form-control p-1"
                                          readOnly
                                          value={
                                            excelSample[row.excel_column] || ""
                                          }
                                        />
                                      </td>
                                      <td className="p-1">
                                        <i
                                          className="ri-delete-bin-line text-danger"
                                          onClick={() => handleRemoveRow(index)}
                                        ></i>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="row mt-3">
                      <div className="col-md-4">
                        <div className="card bg-success text-white">
                          <div className="card-body">
                            Success : {successCount}
                          </div>
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="card bg-danger text-white">
                          <div className="card-body">
                            Failed : {failedCount}
                          </div>
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="card bg-warning">
                          <div className="card-body">
                            Validation Errors : {validationCount}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="card mt-4">
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <h5>Excel Preview</h5>
                          <div className="d-flex gap-2">
                            <button
                              className="btn btn-success"
                              onClick={handleStartImport}
                              disabled={isImporting || !editableData.length}
                            >
                              Start Import
                            </button>
                            <button
                              className="btn btn-warning"
                              onClick={handleRetryFailed}
                              disabled={isImporting}
                            >
                              Retry Failed
                            </button>
                            <button
                              className="btn btn-danger"
                              onClick={downloadFailedRows}
                            >
                              Download Failed
                            </button>
                          </div>
                        </div>

                        <div className="progress mb-3">
                          <div
                            className="progress-bar"
                            role="progressbar"
                            style={{ width: `${progress}%` }}
                          >
                            {progress}%
                          </div>
                        </div>

                        <div className="table-responsive">
                          <table className="table table-bordered table-hover table-sm">
                            <thead className="table-dark">
                              <tr>
                                <th>SN</th>
                                {activeMappings?.map((m, i) => (
                                  <th key={i}>
                                    {m.field} ({m.excel_column})
                                  </th>
                                ))}
                                <th>Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {editableData?.map((row, i) => (
                                <tr
                                  key={i}
                                  className={`${
                                    importStatus[i]?.status === "error"
                                      ? "table-danger"
                                      : validationErrors[i]?.length
                                        ? "table-warning"
                                        : ""
                                  }`}
                                >
                                  <td>{i + 1}</td>
                                  {activeMappings.map((m, j) => (
                                    <td key={j} className="p-1">
                                      <input
                                        type="text"
                                        className="form-control p-1"
                                        value={row[m.field] ?? ""}
                                        onChange={(e) =>
                                          handleCellChange(
                                            i,
                                            m.field,
                                            e.target.value,
                                          )
                                        }
                                      />
                                    </td>
                                  ))}
                                  <td>
                                    {importStatus[i]?.status === "success" && (
                                      <span className="badge bg-success">
                                        Imported
                                      </span>
                                    )}
                                    {importStatus[i]?.status === "error" && (
                                      <>
                                        <span className="badge bg-danger">
                                          Failed
                                        </span>
                                        <button
                                          className="btn btn-warning btn-sm ms-2"
                                          onClick={() => handleRetrySingle(i)}
                                        >
                                          Retry
                                        </button>
                                        <div
                                          className="text-danger mt-1"
                                          style={{ fontSize: "11px" }}
                                        >
                                          {importStatus[i]?.message}
                                        </div>
                                      </>
                                    )}
                                    {!importStatus[i] && (
                                      <span className="badge bg-secondary">
                                        Pending
                                      </span>
                                    )}
                                    {validationErrors[i]?.length > 0 && (
                                      <div
                                        className="text-danger mt-1"
                                        style={{ fontSize: "11px" }}
                                      >
                                        {validationErrors[i].join(", ")}
                                      </div>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
